import graphqlResource, { GraphqlResource } from 'modules/core/graphql-resource';
import { ResponseGraphql } from 'shared/types/response-graphql';
import { ActivePaidSubscriptionDataMapper } from '../data-mappers/active-paid-subscription.data-mapper';
import { OrganizerDataMapper } from '../data-mappers/organizer.data-mapper';
import { SocialNetDataMapper } from '../data-mappers/social-net.data-mapper';

import { ActivePaidSubscription } from '../interfaces/active-paid-subscription';
import { ActivePaidSubscriptionResponse } from '../interfaces/active-paid-subscription-response';
import { Organizer } from '../interfaces/organizer';
import { OrganizerInfoDto } from '../interfaces/organizer-dto';
import { SocialNet } from '../interfaces/social-net';
import { SocialNetResponseDto } from '../interfaces/social-net-dto';
import { UpsertOrganizerInput } from '../interfaces/upsert-organizer-input';
import { UpsertOrganizerResponse } from '../interfaces/upsert-organizer-response';
import { UpsertSocialNetInput, UpsertSocialNetResponseDto } from '../interfaces/upsert-social-net-input';
import { unwrapGraphql } from 'shared/errors';

export class OrganizerRepository {
  constructor(private readonly graphqlResource: GraphqlResource) {}

  async fetchOrganizer(tournamentId: number): Promise<Organizer | null> {
    const query = `#graphql
    query getTournamentDetails($id: Int!) {
      tournament(id: $id) {
        id
        name
        sportType
        email
        phone
        description
        championships: championshipsPaginated(limit: 5, offset: 0) {
          id
          name
          startDate
          endDate
        }
        images {
          type
          externalUrl
          createdAt
        }
        address {
          city
          country
          region
        }
        timezone
      }
    }
    `;

    const response = await this.graphqlResource.query<ResponseGraphql<{ tournament: OrganizerInfoDto }>>(query, { id: tournamentId });

    const dataMapper = new OrganizerDataMapper();
    return dataMapper.decode(unwrapGraphql(response.data).tournament);
  }

  async upsertOrganizer(input: UpsertOrganizerInput): Promise<Organizer> {
    const query = `#graphql
    mutation upsertTournament($input: UpsertTournamentInput!) {
      upsertTournament(input: $input) {
        id
        name
        sportType
        email
        phone
        description
        championships: championshipsPaginated(limit: 5, offset: 0) {
          id
          name
          startDate
          endDate
        }
        images {
          type
          externalUrl
          createdAt
        }
        address {
          city
          country
          region
        }
        timezone
      }
    }
    `;

    const variables = { input };
    const response = await graphqlResource.query<UpsertOrganizerResponse>(query, variables);

    // Раньше `new Error(message)` терял код отказа — теперь `GraphqlError` с кодом (IMGM-1549).
    const dataMapper = new OrganizerDataMapper();
    return dataMapper.decode(unwrapGraphql(response.data).upsertTournament);
  }

  async fetchActivePaidSubscription(tournamentId: number): Promise<ActivePaidSubscription | null> {
    const query = `#graphql
    query ActivePaidSubscription($tournamentId: Int!) {
      activePaidSubscription(tournamentId: $tournamentId) {
        id
        ownerId
        activeFrom
        activeTo
        purchaseType
      }
    }
    `;

    const response = await this.graphqlResource.query<ActivePaidSubscriptionResponse>(query, {
      tournamentId,
    });
    const dataMapper = new ActivePaidSubscriptionDataMapper();
    return dataMapper.decode(unwrapGraphql(response.data).activePaidSubscription);
  }

  async fetchSocialNet(tournamentId: number): Promise<SocialNet[]> {
    const query = `#graphql
    query getOrganizerSocialMedias($tournamentId: Int!) {
      tournament(id: $tournamentId) {
        socialMedias {
          id
          type
          url
        }
      }
    }
    `;

    const response = await this.graphqlResource.query<SocialNetResponseDto>(query, {
      tournamentId,
    });
    const { socialMedias } = unwrapGraphql(response.data).tournament;

    const socialNets: SocialNet[] = [];

    // Add regular social media entries
    if (socialMedias && socialMedias.length) {
      const dataMapper = new SocialNetDataMapper();
      socialNets.push(...dataMapper.decodeArray(socialMedias));
    }

    return socialNets;
  }

  async upsertSocialNet(input: UpsertSocialNetInput): Promise<SocialNet> {
    // Regular case for social media entries
    const query = `#graphql
    mutation upsertSocialMedia($input: UpsertSocialMediaInput!) {
      upsertSocialMedia(input: $input) {
        id
        type
        url
      }
    }
    `;

    const variables = { input };
    const response = await this.graphqlResource.query<UpsertSocialNetResponseDto>(query, variables);
    // Код отказа (17010 — ссылка не подходит для соцсети) доходит до экрана через `GraphqlError`.
    const dataMapper = new SocialNetDataMapper();
    return dataMapper.decode(unwrapGraphql(response.data).upsertSocialMedia);
  }
}

const organizerRepository = new OrganizerRepository(graphqlResource);
export default organizerRepository;
